<template>
  <define-search-button>
    <base-button
      aria-haspopup="menu"
      :aria-label="$t.searchIconLabel"
      class="group"
      @click="openSearchPanel"
    >
      <span
        class="block rounded-xs p-2.5 duration lg:m-1 lg:p-1 group-hover:forced-colors:outline"
        :class="{
          'c-white bg-op-90 group-hover:bg-brand-1': header.transparent
            && !isPassedTransparencyThreshold,
          'group-hover:bg-brand-2': !header.transparent
            || (header.transparent && isPassedTransparencyThreshold),
        }"
      >
        <vf-icon name="search" :size="{ sm: 'md', lg: 'lg' }" />
      </span>
    </base-button>
  </define-search-button>
  <define-cart-button>
    <base-link class="group" to="/cart" @click="$gtm.push('topNav.onClickUtilityLink', 'Cart')">
      <span
        class="relative block rounded-xs p-2.5 duration lg:m-1 lg:p-1 group-hover:forced-colors:outline"
        :class="{
          'c-white bg-op-90 group-hover:bg-brand-1': header.transparent && !isPassedTransparencyThreshold,
          'group-hover:bg-brand-2': !header.transparent
            || (header.transparent && isPassedTransparencyThreshold),
        }"
      >
        <span class="sr-only">
          {{ replaceAll($pluralize($t.cartIconLabel, cart?.totalItems), { count: cart.totalItems }) }}
        </span>
        <vf-icon name="bag" :size="{ sm: 'md', lg: 'lg' }" />
        <client-only>
          <span
            v-if="cart?.totalItems"
            aria-hidden="true"
            class="absolute right-0 top-0 m-2 h-3 w-3 flex select-none center rounded-full bg-brand-3 text-micro c-white ring ring-white lg:m-1 lg:h-4 lg:w-4 lg:text-xs"
            style="line-height: 1"
          >
            {{ cart.totalItems }}
          </span>
        </client-only>
      </span>
    </base-link>
  </define-cart-button>
  <define-ai-button>
    <base-button
      v-style:display="aiDisplayStyle"
      :aria-label="$t.askAi"
      class="group"
      @click="openAiAssistant"
    >
      <span
        class="block rounded-xs p-2.5 duration lg:m-1 lg:p-1 group-hover:forced-colors:outline"
        :class="{
          'bg-op-90 lg:c-white lg:group-hover:bg-brand-1': header.transparent
            && !isPassedTransparencyThreshold,
          'lg:group-hover:bg-brand-2': !header.transparent
            || (header.transparent && isPassedTransparencyThreshold),
        }"
      >
        <vf-icon name="ai" size="lg" />
      </span>
    </base-button>
  </define-ai-button>
  <define-account-button>
    <base-button :aria-label="$t.myAccount" class="group" @click="openAccountPanel">
      <span
        class="block rounded-xs p-2.5 duration lg:m-1 lg:p-1 group-hover:forced-colors:outline"
        :class="{
          'c-white bg-op-90 group-hover:bg-brand-1': header.transparent && !isPassedTransparencyThreshold,
          'group-hover:bg-brand-2': !header.transparent
            || (header.transparent && isPassedTransparencyThreshold),
        }"
      >
        <vf-icon class="pointer-events-none" name="account" :size="{ sm: 'md', lg: 'lg' }" />
      </span>
    </base-button>
  </define-account-button>
  <define-clear-session-button>
    <vf-button
      v-if="showClearSessionButton && buyInStore.storeInfo?.storeId"
      size="tiny"
      @click="buyInStore.clearSession"
    >
      {{ $t.clearSession }}
    </vf-button>
  </define-clear-session-button>
  <define-menu-button>
    <base-button :aria-label="$t.menuIconLabel" class="group lg:mr-a" data-test-id="vf-header-menu-button" @click="openMegaMenu">
      <span
        class="block rounded-xs p-2.5 duration lg:m-1 lg:p-1 group-hover:forced-colors:outline"
        :class="{
          'c-white bg-op-90 group-hover:bg-brand-1': header.transparent && !isPassedTransparencyThreshold,
          'group-hover:bg-brand-2': !header.transparent
            || (header.transparent && isPassedTransparencyThreshold),
        }"
      >
        <vf-icon name="menu" :size="{ sm: 'md', lg: 'lg' }" />
      </span>
    </base-button>
  </define-menu-button>
  <section v-show="!hidden">
    <transition v-if="!hidePromoBarBasedOnRoute" name="unfold">
      <lazy-cms-section
        v-if="megaMenu?.promoBar"
        class="o:bg-black o:c-white"
        data-scroll-el="promo-bar"
        :section="sections.promoBar"
      />
    </transition>
  </section>
  <div
    v-if="header.transparent"
    v-show="!hidden"
    class="pointer-events-none absolute-0 z-1"
    style="background: linear-gradient(to bottom, rgba(0, 0, 0, 0.5), transparent); height: 8rem"
  />
  <template v-if="showAiAssistant">
    <div
      v-show="!hidden"
      v-style:mb="{
        sm: `calc(3rem + ${popUpBounds.height.value}px)`,
        md: '3rem',
      }"
      class="fixed bottom-0 right-0 z-10 mr-6 transform transition lg:hidden"
      :class="{ invisible: !isPassedTransparencyThreshold, hidden: hideFloatingAi }"
    >
      <ai-button
        class="rounded-full bg-brand-2 bg-op-90 backdrop-blur-xs"
        style="box-shadow: 0 2px 4px #262424, 0 0 0 1px #ffffff33 inset"
      />
    </div>
  </template>
  <div v-show="!hidden" class="pointer-within sticky top-0 z-above-header" data-scroll-el="header">
    <header
      ref="headerRef"
      class="pointer-within inset-x-0 bottom-0 flex transform center"
      :class="{
        'duration': !isSomethingElseSticky,
        'absolute top-0 bottom-a': header.transparent,
        '<lg:absolute <lg:top-0 <lg:bottom-a': transparentBackground,
      }"
    >
      <button ref="aiButtonRef" id="ai-button" class="hidden">
        {{ $t.askAi }}
      </button>
      <div class="w-full">
        <div
          class="w-full flex center px-3 py-2 ease-out duration"
          :class="[
            header.transparent && !isPassedTransparencyThreshold ? 'bg-transparent' : 'bg-white',
            transparentBackground && !isPassedTransparencyThreshold ? '<lg:bg-transparent' : '<lg:bg-white',
          ]"
        >
          <section class="w-full flex between lg:hidden">
            <div class="pointer-within">
              <base-link class="mt-2 i-block" to="/">
                <span class="sr-only">{{ isSzaCollab ? 'Vans and SZA collab logo' : `${$t.brand} ${$t.home}` }}</span>
                <vf-icon
                  :class="{ 'c-white': (header.transparent && !isPassedTransparencyThreshold) }"
                  :height="isSzaCollab ? '24' : '23'"
                  :src="customLogo"
                  :width="isSzaCollab ? '104' : '67'"
                />
              </base-link>
            </div>

            <div class="flex">
              <clear-session-button />
              <cart-button />
              <account-button />
              <search-button />
              <menu-button />
            </div>
          </section>
          <section class="grid cols-[1fr_auto_1fr] w-full <lg:hidden">
            <menu-button />
            <div class="flex grow center">
              <!-- Logo -->
              <base-link class="@hactive:-rotate-3 p-2 duration" to="/">
                <span class="sr-only">{{ isSzaCollab ? 'Vans and SZA collab logo' : `${$t.brand} ${$t.home}` }}</span>
                <vf-icon
                  :class="{ 'c-white': header.transparent && !isPassedTransparencyThreshold }"
                  :height="isSzaCollab ? '24' : '23'"
                  :src="customLogo"
                  :width="isSzaCollab ? '104' : '67'"
                />
              </base-link>
            </div>

            <section class="flex lg:ml-a <lg:gap-5" data-test-id="menu-section">
              <clear-session-button />
              <account-button />
              <ai-button v-if="showAiAssistant" />
              <search-button />
              <cart-button />
            </section>
          </section>
        </div>
      </div>
    </header>
  </div>

  <!-- Rendering Mega Menu to SSR for Search Engines -->
  <base-lazy-hydrate when="never">
    <vf-mega-menu class="hidden" :items="megaMenu" :links />
  </base-lazy-hydrate>

  <panel-mega-menu
    class-content="bg-transparent backdrop-blur"
    hide-backdrop
    :position="{ sm: 'bottom', lg: 'left' }"
    size="full"
    @sign-in="DialogSignIn.open({ formLocation: 'modal:single:utility-nav:none' })"
  />
  <panel-account size="full" />
  <panel-search-hub
    v-if="!isChatOpen"
    :class-content="[$feature.tempAskMeAnythingSearchHub ? 'lg:w-190' : 'lg:w-full']"
    size="full"
  />
</template>

<script lang="ts" setup>
import type { FormLocation } from '#types/gtm'
import { HeaderContextKey } from '#commerce/components/vf/header/context'
import { EnrollmentState } from '#root/enums/profile'

defineProps<{ hidden?: boolean }>()

const { isSomethingElseSticky, links, megaMenu, sections } = inject(HeaderContextKey)!
const appConfig = useAppConfig()
const { showClearSessionButton } = appConfig.checkout.buyInStore
const { brandLogo, payment: { logos } } = appConfig.assets
const { unsupportedLocales = [] } = appConfig.components.vf.header.aiAssistant ?? {}
const { $locale, $t, $pluralize, $feature, $gtm } = useNuxtApp()
const auth = useAuthStore()
const buyInStore = useBuyInStoreStore()
const cart = useCartStore()
const { DialogSignIn } = useDialogsStore()
const header = useHeaderStore()
const { popUpBounds } = usePopUp()
const route = useRoute()
const { y } = useScroll(document, { throttle: 200 })
const { enrollmentState } = storeToRefs(useUserStore())

const PanelAccount = createPanel('account')
const PanelMegaMenu = createPanel('mega-menu')
const PanelSearchHub = createPanel($feature.tempAskMeAnythingSearchHub ? 'ask-me-anything-search' : 'search-hub')

const headerRef = toRef(header.refs, 'header')
const megaMenuOpened = ref(false)
const aiButtonRef = ref()
const isChatOpen = ref(false)

const [DefineMenuButton, MenuButton] = createReusableTemplate()
const [DefineCartButton, CartButton] = createReusableTemplate()
const [DefineSearchButton, SearchButton] = createReusableTemplate()
const [DefineAccountButton, AccountButton] = createReusableTemplate()
const [DefineAiButton, AiButton] = createReusableTemplate()
const [DefineClearSessionButton, ClearSessionButton] = createReusableTemplate()

const aiDisplayStyle = computed(() =>
  Object.entries($feature.disableAiNavItemOnRoutes || { sm: ['/**'] }).reduce((acc, [key, patternsToDisable]) => {
    acc[key] = !patternsToDisable.some((patternToDisable) => convertPatternToRegex(patternToDisable).test(route.path)) ? 'inline-flex' : 'none'
    return acc
  }, {})
)

const isSzaCollab = computed(() => route.path.includes('/sza'))

const customLogo = computed(() => {
  const customOtwLogo = $feature.otwStyleRoutes.some((path) => convertPatternToRegex(path).test(route.fullPath))

  if (customOtwLogo) return logos.otw
  if (isSzaCollab.value) return 'https://assets.vans.com/image/upload/v1780991640/vanssza_logo.svg'

  return brandLogo
})

const hidePromoBarBasedOnRoute = computed(() =>
  $feature.disablePromoBarOnRoutes.some((path) => convertPatternToRegex(path).test(route.path)))

const isPDP = computed(() => route.path.includes('/p/'))

const transparentBackground = computed(() => header.transparent || isPDP.value)

const isPassedTransparencyThreshold = computed(() => y.value > header.height.header)

const hideFloatingAi = computed(() => ['/cart', '/customizer'].some((path) => route.path.includes(path)))

const showAiAssistant = computed(() =>
  !$feature.tempAskMeAnythingSearchHub && !unsupportedLocales.includes($locale))

const openAccountPanel = () => {
  if (auth.loggedIn) {
    $gtm.push('topNav.onClickUtilityLink', 'My account')
    return PanelAccount.open()
  }

  $gtm.push('topNav.onClickUtilityLink', 'Sign In')
  DialogSignIn.open({ formLocation: 'modal:single:utility-nav:none' }).then(() => {
    if ($feature.allowSimplifiedEnrollment) {
      const formLocation: FormLocation = 'modal:single:utility-nav:none'
      switch (enrollmentState.value) {
        case EnrollmentState.COMPLETE_PROFILE_MOBILE:
          $gtm.push('authForm.onLoyaltyStep', 'Setup:Step1:phoneNumber', 'Closed', formLocation)
          break
        case EnrollmentState.COMPLETE_PROFILE_PROFILE:
          $gtm.push('authForm.onLoyaltyStep', 'Setup:Step2:nameZipCodeBirthday', 'Closed', formLocation)
          break
        case EnrollmentState.COMPLETE_PROFILE_INTERESTS:
          $gtm.push('authForm.onLoyaltyStep', 'Setup:Step3:interests', 'Closed', formLocation)
          break
      }
    }

    // Open the account panel upon successful sign in nonSimplifiedEnrollment
    if (!$feature.allowSimplifiedEnrollment && auth.loggedIn)
      PanelAccount.open()
  })
}

const openMegaMenu = () => {
  // Dialog/Panels promises are resolved when they close
  PanelMegaMenu.open({ items: megaMenu, links: links.value })
    .then(({ closedReason }: { closedReason?: string } = {}) => {
      megaMenuOpened.value = false
      if (closedReason !== 'navigation' && closedReason !== 'dialog')
        $gtm.push('topNav.onClickHamburgerMenu', false)
    })
  megaMenuOpened.value = true
  $gtm.push('topNav.onClickHamburgerMenu', true)
}

const openSearchPanel = () => {
  $gtm.push('topNav.onClickUtilityLink', 'Search')
  PanelSearchHub.open()
}

const openAiAssistant = () => {
  $gtm.push('topNav.onClickUtilityLink', 'AI Shopping Assistant')
  aiButtonRef.value?.click()
}

const onClarityStateChange = ({ detail }: CustomEvent) => {
  isChatOpen.value = !!(detail.state && ['opened', 'maximized'].includes(detail.state))
}

if ($feature.tempAskMeAnythingSearchHub) {
  onMounted(() => useEventListener(window, 'ClarityState:widgetState', onClarityStateChange))
}
</script>
